import { Injectable, Inject, NgZone, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TokRequest, User } from 'src/app/interfaces/user';
import {
  Pet,
  PetGender,
  PetMissingReport,
  PetMissingReportPublic,
  PetMissingReportStatus,
  PetRehomingRequest,
  PetResidenceStatus,
  PetStatus,
} from 'src/app/interfaces/pet';
import { GeolocationInfo } from 'src/app/interfaces/geolocation';
import { UserService } from '../user/user.service';
import { INFO } from 'src/app/constants/app.constants';
import { CommonUtilService } from '../common/common-util.service';
import { Species } from 'src/app/interfaces/species';
import { isPlatformBrowser } from '@angular/common';

export interface PetClaimingInfo {
  chipNumber: string;
  vetCode: string;
  vetCodeSkipped: boolean;
}

export interface PetClaimingAlternativeInfo {
  phoneNumber: string;
  postcode: string;
  verificationCode?: string;
}

export interface ChipCheckerResults {
  type: string;
  message: string;
  links?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PetService {
  private isBrowser: boolean;

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private ngZone: NgZone,
    private _commonUtilService: CommonUtilService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  saveOne(pet: Pet): Observable<Pet> {
    return this.http.put<Pet>(`${environment.apiUrl}/api/users/me/pets/${pet.id}`, pet);
  }

  sendPetRehomingRequest(pet: PetRehomingRequest): Observable<any> {
    return this.http.post<PetRehomingRequest>(
      `${environment.apiUrl}/api/pets/rehoming-request`,
      pet
    );
  }

  sendPetTokRequest(pet: PetRehomingRequest): Observable<any> {
    return this.http.post<PetRehomingRequest>(
      `${environment.apiUrl}/api/pets/rehome/support-email`,
      pet
    );
  }

  getOneFromUser(user: User, petId: number): Pet {
    return user.pets.find((pet) => pet.id === petId);
  }

  getMissingFromUser(user: User, reportId: number): Pet {
    return user.pets.find((pet) => pet.missingPetReports.find((report) => report.id === reportId));
  }

  claimPet(claimingInfo: PetClaimingInfo): Observable<Pet> {
    claimingInfo = {
      chipNumber: claimingInfo.chipNumber.replace(/\s/g, ''),
      vetCode: claimingInfo.vetCode.replace(/\s/g, ''),
      vetCodeSkipped: claimingInfo.vetCodeSkipped,
    };
    return this.http.post<Pet>(`${environment.apiUrl}/api/users/me/pets/claim`, claimingInfo);
  }

  getGender(gender: PetGender): string {
    if (gender === PetGender.Male) {
      return 'Male';
    }
    if (gender === PetGender.Female) {
      return 'Female';
    }
    return 'Unknown';
  }

  getTokFromChipNumber(chipNumber: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/rehome/pets?chipnumber=${chipNumber}`);
  }

  markPetAsDeceased(petId: number, isSubscriptionCancellationRequested): Observable<any> {
    return this.http.put(`${environment.apiUrl}/api/users/me/pets/${petId}/passed-away`, {}, {
      params: {
        isSubscriptionCancellationRequested
      }
    });
  }

  archivePet(petId: number): Observable<any> {
    return this.http.put(`${environment.apiUrl}/api/users/me/pets/${petId}/archive`, {});
  }

  unarchivePet(petId: number): Observable<any> {
    return this.http.put(`${environment.apiUrl}/api/users/me/pets/${petId}/unarchive`, {});
  }

  updateResidenceStatus(petId: number, status: PetResidenceStatus): Observable<any> {
    let statusName = 'unknown';
    if (status === PetResidenceStatus.AtHome) {
      statusName = 'home';
    } else if (status === PetResidenceStatus.Lost) {
      statusName = 'missing';
    } else if (status === PetResidenceStatus.Stolen) {
      statusName = 'stolen';
    }
    return this.http.put(
      `${environment.apiUrl}/api/users/me/pets/${petId}/residence/${statusName}`,
      {}
    );
  }

  getCertificate(chipNumber: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/chips/certificate/${chipNumber}`);
  }

  doesChipExistInDb(chipNumber: string): Observable<any> {
    return this.http.get(`${environment.apiUrl}/api/chips/registered/${chipNumber}`);
  }

  checkChip(chipNumber: string): Observable<ChipCheckerResults> {
    return this.http.get<any>(`${environment.apiUrl}/api/chips/checkchip/${chipNumber}`).pipe(
      map((results) => {
        const chipProviders = results.filter((result) => result.chipIsRegistered === true);
        if (chipProviders.length) {
          return {
            type: 'success',
            message: 'Details of this microchip can be found at:',
            links: chipProviders.map((p) => p.homepageUrl),
          };
        }

        const chipInfoProviders = results.filter((result) => result.queryResponse === INFO);
        if (chipInfoProviders.length) {
          return {
            type: 'success',
            message: 'Information about this microchip can be found at:',
            links: chipInfoProviders.map((p) => p.homepageUrl),
          };
        }

        return {
          type: 'danger',
          message:
            'Sorry, your chip number has not been found on any of the UK databases that we have checked. Please ensure that you have entered the chip number correctly.',
        };
      }),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && err.status === 400) {
          return of({
            type: 'danger',
            message:
              'Sorry, your chip number has not been found on any of the UK databases that we have checked. Please ensure that you have entered the chip number correctly.',
          });
        }
        return of({
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        });
      })
    );
  }

  reportMissing(report: PetMissingReport, petId: number): Observable<any> {
    return this.http.put(`${environment.apiUrl}/api/users/me/pets/${petId}/report-missing`, report);
  }

  reportMissingUpdate(report: PetMissingReport, reportId: number): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}/api/users/me/pets/${reportId}/missing-update`,
      report
    );
  }

  reportFound(petId: number, reportStatus: PetMissingReportStatus): Observable<any> {
    return this.http.put(
      `${environment.apiUrl}/api/users/me/pets/${petId}/report-found/${reportStatus}`,
      {}
    );
  }

  getAllSpecies(): Observable<Species[]> {
    return this.http.get<Species[]>(`${environment.apiUrl}/api/species/all/filtered`).pipe(
      map((species) => {
        species.sort(); // Sorting alphabetically
        // Get all cat related species and move to the top (it should be one but will try to handle some unexpected scenario)
        const catRegExp = /(^cat\s|\scat$|\scat\s)/i;
        species.sort((a, b) => {
          if (!catRegExp.test(a.description) && catRegExp.test(b.description)) {
            return 1;
          }
          if (catRegExp.test(a.description) && !catRegExp.test(b.description)) {
            return -1;
          }
          return 0;
        });
        // Get all dog related species and move to the top (it should be one but will try to handle some unexpected scenario)
        const dogRegExp = /(^dog\s|\sdog$|\sdog\s)/i;
        species.sort((a, b) => {
          if (!dogRegExp.test(a.description) && dogRegExp.test(b.description)) {
            return 1;
          }
          if (dogRegExp.test(a.description) && !dogRegExp.test(b.description)) {
            return -1;
          }
          return 0;
        });
        return species;
      })
    );
  }

  getBreedsFor(speciesCode: number): Observable<{ [name: string]: number }> {
    return this.http.get<any>(
      `${environment.apiUrl}/api/breed-codes-by-names/for/species/${speciesCode}`
    );
  }

  closeTokStatus(chipNumber: string, status: number) {
    return this.http.post(
      `${environment.apiUrl}/api/rehome/${chipNumber}/close?status=${status}`, null
    )
  }
  
  getPetMissingReport(id: number, user?: User): Observable<any> {
    return new Observable((subscriber) => {
      // If a user is provided, check if a missing pet report is found on any pets registered under the user
      const report = user?.pets.map((p) => {
        const pr = p.missingPetReports.find((r) => r.id === id) as any;
        if (!pr || !this.userService.isUserBasicOrBasicPlus(user.productType)) {
          return null;
        }
        pr.pet = p;
        return pr as PetMissingReportPublic;
      })[0];

      if (report) {
        this.getLocationInfo(report.lastSeen.location)
          .subscribe((locationInfo) => {
            report.lastSeen.location = locationInfo;
          })
          .add(() => {
            subscriber.next(report);
            subscriber.complete();
          });
      } else {
        this.http
          .get<PetMissingReportPublic>(`${environment.apiUrl}/api/pets/missing/${id}`)
          .subscribe(
            (missingPetReport) => {
              this.getLocationInfo(missingPetReport.lastSeen.location)
                .subscribe((locationInfo) => {
                  missingPetReport.lastSeen.location = locationInfo;
                })
                .add(() => {
                  subscriber.next(missingPetReport);
                  subscriber.complete();
                });
            },
            (error) => {
              subscriber.error(error);
            }
          );
      }
    });
  }

  getMissingReportsForPet(petId: number): Observable<PetMissingReport[]> {
    return new Observable((subscriber) => {
      this.http
        .get<PetMissingReportPublic[]>(
          `${environment.apiUrl}/api/pets/missing/${petId}/all?includeDrafts=true`
        )
        .subscribe(
          (missingPetReports) => {
            missingPetReports.forEach((missingPetReport) => {
              this.getLocationInfo(missingPetReport.lastSeen.location).subscribe((locationInfo) => {
                missingPetReport.lastSeen.location = locationInfo;
              });
            });
            subscriber.next(missingPetReports);
            subscriber.complete();
          },
          (error) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              subscriber.next([]);
              subscriber.complete();
            } else {
              subscriber.error(error);
            }
          }
        );
    });
  }

  public getListOfPetsBasedOnLifeStatusAndDate(
    pets: Pet[],
    numberOfDaysBetween: number,
    showArchivedPets: boolean
  ) {
    const filteredListOfPets = [];
    pets.forEach((pet) => {
      if (!showArchivedPets && pet.archived === true) {
        return;
      }
      if (
        pet.status == PetStatus.Deceased &&
        !this._commonUtilService.checkIfDateIsWithinGivenDays(pet.updated, numberOfDaysBetween)
      ) {
        return;
      }

      filteredListOfPets.push(pet);
    });

    return filteredListOfPets;
  }

  private getLocationInfo(location: GeolocationInfo): Observable<GeolocationInfo> {
    return new Observable((subscriber) => {

      if (!this.isBrowser) {
        subscriber.next(location);
        subscriber.complete();
        return;
      }

      const geocoder = new google.maps.Geocoder();
      const latLng = new google.maps.LatLng({
        lat: location.latitude,
        lng: location.longitude,
      });

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results[0]) {
          const town = results[0].address_components.find((addr) =>
            addr.types.includes('postal_town')
          );
          const postcode = results[0].address_components.find((addr) =>
            addr.types.includes('postal_code')
          );
          this.ngZone.run(() => {
            subscriber.next({
              latitude: location.latitude,
              longitude: location.longitude,
              info: {
                postcode: postcode?.long_name.split(' ')[0],
                town: town?.long_name,
              },
            });
            subscriber.complete();
          });
        } else {
          this.ngZone.run(() => {
            subscriber.next(location);
            subscriber.complete();
          });
        }
      });
    });
  }

  getTokRequestPet(tokRequests: TokRequest[], userPets: Pet[]): Pet {
    const tokRequestPetChipNumber = tokRequests.find((tokRequest) => tokRequest.status === 3).chipNumber;
    return userPets.find((pet) => pet.chipNumber === tokRequestPetChipNumber);
  }
}
